import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Briefcase, FileText, DollarSign, Calendar, Edit, Brain } from 'lucide-react'
import { portfoliosService, Portfolio } from '../services/portfolios'
import { projectsService, Project } from '../services/projects'
import { toast } from 'sonner'
import { getErrorMessage } from '../utils/apiTransformers'
import { aiInsightsService, AIInsight } from '../services/aiInsights'
import { InsightCard } from '../components/ui/InsightCard'
import { PredictionCard } from '../components/ui/PredictionCard'
import type { ApiErrorLike } from '../utils/apiError'

export default function PortfolioWorkspace() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [insights, setInsights] = useState<AIInsight[]>([])
  const [loading, setLoading] = useState(true)
  const [projectsLoading, setProjectsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    if (id) {
      fetchPortfolio()
    }
  }, [id])

  useEffect(() => {
    if (id && activeTab === 'projects') {
      fetchProjects()
    }
  }, [id, activeTab])

  const fetchPortfolio = async () => {
    if (!id) {
      navigate('/portfolio')
      return
    }
    const portfolioId = parseInt(id)
    if (isNaN(portfolioId)) {
      toast.error('Invalid portfolio ID')
      navigate('/portfolio')
      return
    }
    setLoading(true)
    try {
      const [data, insightData] = await Promise.all([
        portfoliosService.get(portfolioId),
        // Sprint 11: was 'organization' — that queried for org-scoped insights
        // keyed by the portfolio id, so every portfolio either showed zero
        // insights or the same unrelated rows. 'portfolio' is the right kind.
        aiInsightsService.list({ entity_type: 'portfolio', entity_id: portfolioId, limit: 3 }).catch(() => []),
      ])
      setPortfolio(data)
      setInsights(insightData)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching portfolio:', error)
      toast.error(getErrorMessage(error) || 'Failed to load portfolio')
      navigate('/portfolio')
    } finally {
      setLoading(false)
    }
  }

  const fetchProjects = async () => {
    if (!id) return
    const portfolioId = parseInt(id)
    if (isNaN(portfolioId)) {
      console.error('Invalid portfolio ID:', id)
      return
    }
    setProjectsLoading(true)
    try {
      const data = await projectsService.list({ portfolio_id: portfolioId })
      setProjects(data)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching projects:', error)
      toast.error(getErrorMessage(error) || 'Failed to load projects')
    } finally {
      setProjectsLoading(false)
    }
  }

  // Calculate portfolio stats
  const portfolioStats = {
    totalProjects: projects.length,
    totalBudget: projects.reduce((sum, p) => sum + parseFloat(p.budget || '0'), 0),
    totalActualCost: projects.reduce((sum, p) => sum + parseFloat(p.actual_cost || '0'), 0),
    averageProgress: projects.length > 0 
      ? Math.round(projects.reduce((sum, p) => sum + p.progress, 0) / projects.length)
      : 0,
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'projects', label: 'Projects', icon: Briefcase },
    { id: 'budget', label: 'Budget', icon: DollarSign },
    { id: 'timeline', label: 'Timeline', icon: Calendar },
  ]

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#f0f4f8]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#486581] mx-auto mb-4"></div>
          <p className="text-[#486581]">Loading portfolio...</p>
        </div>
      </div>
    )
  }

  if (!portfolio) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#f0f4f8]">
        <div className="text-center">
          <Briefcase className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-[#102a43]">Portfolio not found</p>
          <button
            onClick={() => navigate('/portfolio')}
            className="mt-4 px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692]"
          >
            Back to Portfolios
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-[#f0f4f8]">
      {/* Left Sidebar */}
      <div className="w-64 bg-[#486581] border-r border-[#5C748E] overflow-y-auto flex-shrink-0">
        <button
          onClick={() => navigate('/portfolio')}
          className="w-full flex items-center gap-2 px-4 py-3 text-[#C5CDD6] hover:bg-[#3D4F61] border-b border-[#3D4F61]"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Portfolios</span>
        </button>

        <div className="p-4 border-b border-[#3D4F61]">
          <h2 className="font-medium mb-1 text-white text-sm truncate">{portfolio.name}</h2>
          <p className="text-xs text-[#A3B1BF] mb-3">ID: {portfolio.id}</p>
        </div>

        <div className="p-2">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg mb-1 transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#DAA520] text-white'
                    : 'text-[#C5CDD6] hover:bg-[#3D4F61] hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm">{tab.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'overview' && (
          <div className="p-6 space-y-6">
            {/* Portfolio Header */}
            <div className="mb-6">
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h1 className="text-3xl font-semibold text-[#2A3B4C] mb-2">{portfolio.name}</h1>
                  <p className="text-lg text-[#556B7C]">{portfolio.description || 'No description available'}</p>
                </div>
                <button
                  onClick={() => navigate(`/portfolio/${id}/edit`)}
                  className="px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Portfolio
                </button>
              </div>
              <div className="mb-4">
                <PredictionCard entityType="portfolio" entityId={portfolio.id} />
              </div>
              {/* Stats Cards */}
              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: 'Status', value: portfolio.status.replace('_', ' ') },
                  { label: 'Projects', value: portfolioStats.totalProjects.toString() },
                  { label: 'Total Budget', value: `$${portfolioStats.totalBudget.toLocaleString()}` },
                  { label: 'Avg Progress', value: `${portfolioStats.averageProgress}%` },
                ].map((stat, index) => (
                  <div key={index} className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
                    <div className="text-sm text-[#6F8394] mb-2">{stat.label}</div>
                    <div className="text-xl font-semibold text-[#2A3B4C]">{stat.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Insights */}
            {insights.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="w-4 h-4 text-[#DAA520]" />
                  <h2 className="text-sm font-semibold text-[#2A3B4C]">AI Insights</h2>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {insights.map(i => (
                    <InsightCard key={i.id} insight={i} onFeedback={(id, score) => aiInsightsService.submitFeedback(id, score)} />
                  ))}
                </div>
              </section>
            )}

            {/* Portfolio Details */}
            <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
              <h2 className="text-lg font-semibold text-[#102a43] mb-4">Portfolio Details</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-[#829ab1] mb-2">Description</h3>
                  <p className="text-[#102a43]">{portfolio.description || 'No description available'}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-[#829ab1] mb-2">Status</h3>
                    <span className={`px-3 py-1 rounded-lg border-2 capitalize ${
                      portfolio.status === 'active' ? 'bg-green-100 text-green-700 border-green-300' :
                      portfolio.status === 'planning' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                      portfolio.status === 'on_hold' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                      portfolio.status === 'completed' ? 'bg-gray-100 text-gray-700 border-gray-300' :
                      'bg-red-100 text-red-700 border-red-300'
                    }`}>
                      {portfolio.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-[#829ab1] mb-2">Created</h3>
                    <p className="text-[#102a43]">{new Date(portfolio.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="p-6 space-y-6">
            <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#102a43]">Portfolio Projects</h2>
                <button
                  onClick={() => navigate(`/project/create?portfolio_id=${id}`)}
                  className="px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors flex items-center gap-2"
                >
                  <Briefcase className="w-4 h-4" />
                  Add Project
                </button>
              </div>

              {projectsLoading ? (
                <div className="text-center py-8 text-[#829ab1]">Loading projects...</div>
              ) : projects.length === 0 ? (
                <div className="text-center py-8 text-[#829ab1]">
                  <Briefcase className="w-12 h-12 mx-auto mb-4 text-[#bcccdc]" />
                  <p>No projects found in this portfolio.</p>
                  <button
                    onClick={() => navigate(`/project/create?portfolio_id=${id}`)}
                    className="mt-4 px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors"
                  >
                    Add First Project
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[#bcccdc]">
                    <thead className="bg-[#f0f4f8]">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[#829ab1] uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[#829ab1] uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[#829ab1] uppercase tracking-wider">
                          Progress
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-[#829ab1] uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#bcccdc]">
                      {projects.map((project) => (
                        <tr key={project.id} className="hover:bg-[#f0f4f8] transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-[#102a43]">{project.name}</div>
                            {project.description && (
                              <div className="text-sm text-[#829ab1] truncate max-w-md">{project.description}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${
                              project.status === 'active' ? 'bg-green-100 text-green-700 border-green-300' :
                              project.status === 'planning' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                              project.status === 'on_hold' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                              project.status === 'completed' ? 'bg-gray-100 text-gray-700 border-gray-300' :
                              'bg-red-100 text-red-700 border-red-300'
                            }`}>
                              {project.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="w-24 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-[#486581] h-2 rounded-full"
                                  style={{ width: `${project.progress}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-[#829ab1]">{project.progress}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => navigate(`/project/${project.id}`)}
                              className="text-[#486581] hover:text-[#102a43] transition-colors"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'budget' && (
          <div className="p-6 space-y-6">
            <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
              <h2 className="text-lg font-semibold text-[#102a43] mb-4">Portfolio Budget Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <div className="text-sm text-[#829ab1] mb-1">Total Budget</div>
                  <div className="text-2xl font-semibold text-[#102a43]">
                    ${portfolioStats.totalBudget.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-[#829ab1] mb-1">Actual Cost</div>
                  <div className="text-2xl font-semibold text-[#102a43]">
                    ${portfolioStats.totalActualCost.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-[#829ab1] mb-1">Remaining</div>
                  <div className={`text-2xl font-semibold ${
                    portfolioStats.totalBudget - portfolioStats.totalActualCost >= 0
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    ${(portfolioStats.totalBudget - portfolioStats.totalActualCost).toLocaleString()}
                  </div>
                </div>
              </div>
              {portfolioStats.totalBudget > 0 && (
                <div>
                  <div className="text-sm text-[#829ab1] mb-2">Budget Utilization</div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className={`h-4 rounded-full ${
                        (portfolioStats.totalActualCost / portfolioStats.totalBudget) * 100 > 100
                          ? 'bg-red-600'
                          : (portfolioStats.totalActualCost / portfolioStats.totalBudget) * 100 > 80
                          ? 'bg-yellow-600'
                          : 'bg-green-600'
                      }`}
                      style={{
                        width: `${Math.min(100, (portfolioStats.totalActualCost / portfolioStats.totalBudget) * 100)}%`,
                      }}
                    ></div>
                  </div>
                  <div className="mt-2 text-sm text-[#829ab1]">
                    {((portfolioStats.totalActualCost / portfolioStats.totalBudget) * 100).toFixed(1)}% utilized
                  </div>
                </div>
              )}
            </div>

            {/* Project Budget Breakdown */}
            {projects.length > 0 && (
              <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
                <h2 className="text-lg font-semibold text-[#102a43] mb-4">Project Budget Breakdown</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[#bcccdc]">
                    <thead className="bg-[#f0f4f8]">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[#829ab1] uppercase tracking-wider">
                          Project
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[#829ab1] uppercase tracking-wider">
                          Budget
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[#829ab1] uppercase tracking-wider">
                          Actual Cost
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[#829ab1] uppercase tracking-wider">
                          Variance
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#bcccdc]">
                      {projects.map((project) => {
                        const budget = parseFloat(project.budget || '0')
                        const actual = parseFloat(project.actual_cost || '0')
                        const variance = budget - actual
                        return (
                          <tr key={project.id} className="hover:bg-[#f0f4f8] transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-[#102a43]">{project.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#829ab1]">
                              ${budget.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#829ab1]">
                              ${actual.toLocaleString()}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                              variance >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {variance >= 0 ? '+' : ''}${variance.toLocaleString()}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="p-6 space-y-6">
            <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
              <h2 className="text-lg font-semibold text-[#102a43] mb-4">Portfolio Timeline</h2>
              {projects.length === 0 ? (
                <p className="text-[#829ab1]">No projects in this portfolio.</p>
              ) : (
                <div className="space-y-4">
                  {projects
                    .filter((p) => p.start_date || p.end_date)
                    .map((project) => (
                      <div key={project.id} className="border-l-4 border-[#486581] pl-4 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-[#102a43]">{project.name}</h3>
                            <div className="text-sm text-[#829ab1] mt-1">
                              {project.start_date && (
                                <span>Start: {new Date(project.start_date).toLocaleDateString()}</span>
                              )}
                              {project.start_date && project.end_date && ' • '}
                              {project.end_date && (
                                <span>End: {new Date(project.end_date).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`px-2 py-1 text-xs font-semibold rounded-full border ${
                              project.status === 'active' ? 'bg-green-100 text-green-700 border-green-300' :
                              project.status === 'planning' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                              project.status === 'on_hold' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                              project.status === 'completed' ? 'bg-gray-100 text-gray-700 border-gray-300' :
                              'bg-red-100 text-red-700 border-red-300'
                            }`}>
                              {project.status.replace('_', ' ')}
                            </div>
                            <div className="text-xs text-[#829ab1] mt-1">Progress: {project.progress}%</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  {projects.filter((p) => !p.start_date && !p.end_date).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[#bcccdc]">
                      <p className="text-sm text-[#829ab1] mb-2">
                        Projects without dates ({projects.filter((p) => !p.start_date && !p.end_date).length}):
                      </p>
                      {projects
                        .filter((p) => !p.start_date && !p.end_date)
                        .map((project) => (
                          <div key={project.id} className="text-sm text-[#102a43]">
                            • {project.name}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

